import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import callApi from '../../Api/config';
import { REJECTED_APPLICATIONS } from '../../constants/EntityName';
import { AppRoutes } from '../../constants/AppConstant';
import { toastError } from '../../service/Toast';
import { formatIsoDate } from '../../utils/functions';
import DefaultTableHeader from '../Entity/AbstractLanding/DefaultTableHeader';
import AbstractTabs from '../Entity/AbstractTabs';

interface RejectedApplication {
  memberId: string;
  applicantName: string;
  orderId: string;
  customerName: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  serviceName: string | null;
  packageName: string | null;
  paymentStatus: string | null;
  amountPaid: number;
  currentStatus: string | null;
  rejectionRemark: string | null;
  rejectedAt: string;
  refund: { refundRequestId: string; status: string; amount: number } | null;
}

// Refund request (raised by the customer) — shown only to track possible refunds.
const REFUND_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Refund Pending',
  APPROVED: 'Refund Approved',
  REJECTED: 'Refund Rejected',
  REFUNDED: 'Refunded',
};

const REFUND_STATUS_COLORS: Record<string, string> = {
  PENDING: 'gold',
  APPROVED: 'green',
  REJECTED: 'red',
  REFUNDED: 'blue',
};

/** Transactions → Rejected Orders: applicants rejected by authority, for refund tracking. */
const RejectedOrders = () => {
  const history = useHistory();
  const [rows, setRows] = useState<RejectedApplication[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSearch, setFilterSearch] = useState<Record<string, any>>({});
  const [totalCount, setTotalCount] = useState(0);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  const fetchRejected = useCallback(async () => {
    try {
      setLoading(true);
      const res = await callApi(REJECTED_APPLICATIONS, 'GET', null, {
        page: pagination.current,
        limit: pagination.pageSize,
        search: searchQuery.trim() || undefined,
        serviceName: filterSearch.serviceName || undefined,
        startDate: filterSearch.startDate || undefined,
        endDate: filterSearch.endDate || undefined,
      });
      if (res?.status && res?.data?.data) {
        setRows(res.data.data.results || []);
        setTotalCount(res.data.data.totalCount || 0);
      } else {
        toastError(res?.data?.message || 'Failed to fetch rejected orders');
      }
    } catch (error) {
      toastError('Failed to fetch rejected orders');
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, searchQuery, filterSearch]);

  useEffect(() => {
    fetchRejected();
  }, [fetchRejected]);

  const openOrder = (orderId: string) =>
    history.push(`${AppRoutes.DocumitraOrderApplication}?search=${encodeURIComponent(orderId)}`);

  const columns: ColumnsType<RejectedApplication> = [
    {
      title: 'Rejected Date',
      dataIndex: 'rejectedAt',
      key: 'rejectedAt',
      render: (value: string) => (value ? formatIsoDate(value) : '--'),
    },
    {
      title: 'Order ID',
      dataIndex: 'orderId',
      key: 'orderId',
      render: (orderId: string) =>
        orderId ? (
          <a 
          style={{ color: '#323232', textDecoration: 'underline' }}
            href={`${AppRoutes.DocumitraOrderApplication}?search=${encodeURIComponent(orderId)}`}
            onClick={event => {
              event.preventDefault();
              openOrder(orderId);
            }}
          >
            {orderId}
          </a>
        ) : (
          '--'
        ),
    },
    { title: 'Member Name', dataIndex: 'applicantName', key: 'applicantName' },
    {
      title: 'Application Status',
      key: 'applicationStatus',
      render: (_: unknown, record) => (
        <div>
          <Tag color="red">Rejected by Authority</Tag>
          {record.currentStatus && (
            <div style={{ color: '#64748b', fontSize: 12, marginTop: 4 }}>
              Now: {record.currentStatus}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'User',
      key: 'customer',
      render: (_: unknown, record) => (
        <div>
          <div>{record.customerName || '--'}</div>
          <div style={{ color: '#64748b', fontSize: 12 }}>{record.customerEmail || ''}</div>
        </div>
      ),
    },
    // {
    //   title: 'Mobile',
    //   dataIndex: 'customerPhone',
    //   key: 'customerPhone',
    //   render: (value: string | null) => value || '--',
    // },
    {
      title: 'Service / Package',
      key: 'service',
      render: (_: unknown, record) => (
        <div>
          <div>{record.serviceName || '--'}</div>
          <div style={{ color: '#64748b', fontSize: 12 }}>{record.packageName || ''}</div>
        </div>
      ),
    },
    {
      title: 'Payment Status',
      dataIndex: 'paymentStatus',
      key: 'paymentStatus',
      render: (value: string | null) => value || '--',
    },
    // {
    //   title: 'Amount Paid',
    //   dataIndex: 'amountPaid',
    //   key: 'amountPaid',
    //   render: (value: number) => `$${Number(value || 0).toFixed(2)}`,
    // },
    {
      title: 'Rejection Remark',
      dataIndex: 'rejectionRemark',
      key: 'rejectionRemark',
      render: (value: string | null) => value || '--',
    },
    {
      title: 'Refund Request',
      key: 'refund',
      render: (_: unknown, record) =>
        record.refund ? (
          <span>
            {record.refund.refundRequestId}{' '}
            <Tag color={REFUND_STATUS_COLORS[record.refund.status] || 'default'}>
              {REFUND_STATUS_LABELS[record.refund.status] || record.refund.status}
            </Tag>
          </span>
        ) : (
          <Tag>Not raised</Tag>
        ),
    },
  ];

  return (
    <AbstractTabs
      activeKey="0"
      entityName="Rejected Orders"
      onTabClick={() => {}}
      updateFormVisibility={() => {}}
      content={[]}
    >
      <div>
        <DefaultTableHeader
          headerName="Rejected Orders"
          totalCount={totalCount}
          countText="Rejected Applications"
          setSearchQuery={(value: any) => {
            setSearchQuery(typeof value === 'string' ? value : String(value || ''));
            setPagination(prev => ({ ...prev, current: 1 }));
          }}
          entityName={REJECTED_APPLICATIONS}
          showFilter={true}
          filterQuery={(filters: any) => {
            setFilterSearch(filters || {});
            setPagination(prev => ({ ...prev, current: 1 }));
          }}
          disableAdd={true}
          showDownload={false}
        />
        <Table
          rowKey={record => record.memberId}
          columns={columns}
          dataSource={rows}
          loading={loading}
          bordered
          scroll={{ x: 'max-content' }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: totalCount,
            showSizeChanger: true,
            showTotal: (total: number) => `Total ${total} rejected applications`,
            onChange: (page: number, pageSize: number) =>
              setPagination({ current: page, pageSize: pageSize || 10 }),
          }}
        />
      </div>
    </AbstractTabs>
  );
};

export default RejectedOrders;
